import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { FilterConfig, OrderedFilterConfig } from './filter-legend-config';
import { FilterModel } from '../../../../models/locations-models/filter-map';
import { Bubbles } from '../../../../models/locations-models/bubbles';
import { cloneDeep } from 'lodash';
import { LSCField } from '../../../../models/LSCFields';
import { LocationListApiService } from '../location-list-api.service';
import { LocationListLocalStoreService } from '../location-list-local-store.service';
import { MatTooltip } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { LetDirective } from '@ngrx/component';

@Component({
    selector: 'app-status-filter-legend',
    templateUrl: './status-filter-legend.component.html',
    styleUrls: ['../../../../../assets/scss/partials/status-filter-icon-styling.scss',
                './status-filter-legend.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        NgClass,
        MatTooltip,
    ],
})
export class StatusFilterLegendComponent implements AfterViewInit, OnDestroy {

  // 1050px is more or less where the filters start overflowing.
  // Make sure to increase this when more quick filters are added.
  private readonly statusFilterLegendMaxWidth = 1060;

  private isSmallViewport = new BehaviorSubject<boolean>(false);
  private resizeObserver: ResizeObserver;

  readonly viewModel$: Observable<{
    filterModel: FilterModel,
    bubbles: Bubbles,
    isSmallViewport: boolean,
    orderedFilterConfig: FilterConfig[] }> =
      this.locationListLocalStore.select({
        filterModel: this.locationListLocalStore.filterModel$,
        bubbles: this.locationListLocalStore.bubbles$,
        isSmallViewport: this.isSmallViewport.asObservable(),
        orderedFilterConfig: of(OrderedFilterConfig)
      });

  private componentDestroyed$ = new Subject<void>();

  @ViewChild('statusFilterLegend') htmlContainerElement;

  constructor(
      private zone: NgZone,
      private locationListApi: LocationListApiService,
      private locationListLocalStore: LocationListLocalStoreService) {
  }

  ngAfterViewInit() {
    // toggle minimized filter bubbles if the width of the status bar gets too narrow
    // requestAnimationFrame() to fix https://stackoverflow.com/questions/76187282/how-to-fix-resizeobserver-loop-completed-with-undelivered-notifications
    this.resizeObserver = new ResizeObserver((entries) => requestAnimationFrame(() => {
      // ResizeObserver callback events run outside the zone -> add it for change detection
      this.zone.run(() => {
        const newWidth = entries[0].contentRect.width;
        this.isSmallViewport.next(newWidth < this.statusFilterLegendMaxWidth);
      });
    }));
    this.resizeObserver.observe(this.htmlContainerElement.nativeElement);
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.resizeObserver.unobserve(this.htmlContainerElement.nativeElement);
  }

  /**
   * Toggle bubble filters
   * @param filterModel
   * @param statusFilterID
   */
  toggleStatusFilter(
      filterModel: FilterModel,
      statusFilterID: string) {
    const filterModelCopy = cloneDeep(filterModel);
    const statusFilterIndex = filterModel.status?.indexOf(statusFilterID);

    if (statusFilterIndex >= 0) {
      filterModelCopy.status.splice(statusFilterIndex, 1);
    } else {
      filterModelCopy.status ?
          filterModelCopy.status.push(statusFilterID) :
          filterModelCopy[LSCField.status] = [statusFilterID];
    }

    this.locationListApi.setFilters(filterModelCopy);
  }


}
